import React, { useState } from 'react';
import { usePullToRefresh, TRIGGERED_EVENT as PR_EVENT } from '../../hooks/usePullToRefresh';

interface PullToRefreshProps {
  enabled?: boolean;
  children: React.ReactNode;
}

/**
 * Wraps the admin content with a pull-to-refresh gesture (Google/Facebook
 * style). A small spinner pulls down from the top of the page. On release past
 * the threshold the wrapped children are remounted (via a changing key), which
 * re-runs every page's data-fetch effect — so the gesture refreshes ALL admin
 * screens uniformly. It also dispatches `catcare:refresh` for any component
 * listening to that event.
 */
export const PullToRefresh: React.FC<PullToRefreshProps> = ({ enabled = true, children }) => {
  const [reloadKey, setReloadKey] = useState(0);
  const { pull, refreshing } = usePullToRefresh({
    enabled,
  });

  // Refresh when the global pull-to-refresh event fires (remount the page
  // content so every screen re-runs its data-fetch effect).
  React.useEffect(() => {
    const listener = () => {
      setReloadKey((k) => k + 1);
    };
    window.addEventListener(PR_EVENT, listener);
    return () => window.removeEventListener(PR_EVENT, listener);
  }, []);

  const visible = pull > 0 || refreshing;
  const offset = refreshing ? 44 : pull;

  return (
    <div className="relative">
      {visible && (
        <div
          className="pointer-events-none fixed inset-x-0 top-16 z-[60] flex justify-center"
          style={{ transform: `translateY(${offset}px)`, opacity: refreshing ? 1 : Math.min(pull / 40, 1) }}
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white dark:bg-base-900 shadow-md border border-base-200 dark:border-base-700">
            <svg className="h-5 w-5 animate-spin text-accent-500" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
              <path className="opacity-75" fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-3a7 7 0 0 0-7-7V2z" />
            </svg>
          </div>
        </div>
      )}
      <div key={reloadKey}>{children}</div>
    </div>
  );
};
